import { getCategoryConfig, getAllCategoryConfigs } from '../config/categoryConfigs';

/**
 * Category management utilities
 */

/**
 * Get services data source key for a category
 */
export const getCategoryDataSource = (categoryType) => {
  const config = getCategoryConfig(categoryType);
  return config?.getMetadata('dataSource');
};

/**
 * Get category filter key for data filtering
 */
export const getCategoryFilterKey = (categoryType) => {
  const config = getCategoryConfig(categoryType);
  return config?.getMetadata('filterKey');
};

/**
 * Get page configuration key for category
 */
export const getCategoryPageConfigKey = (categoryType) => {
  const config = getCategoryConfig(categoryType);
  return config?.getMetadata('pageConfigKey');
};

/**
 * Generate category route path
 */
export const getCategoryRoutePath = (categoryType) => {
  const config = getCategoryConfig(categoryType);
  return config ? `/services/categories/${config.name}` : null;
};

/**
 * Generate category breadcrumbs
 */
export const getCategoryBreadcrumbs = (categoryType) => {
  const config = getCategoryConfig(categoryType);
  if (!config) return [];

  return [
    { label: 'Home', path: '/' },
    { label: 'Services', path: '/services' },
    { label: config.displayName, path: getCategoryRoutePath(categoryType) }
  ];
};

/**
 * Get category SEO metadata
 */
export const getCategorySEOMetadata = (categoryType) => {
  const config = getCategoryConfig(categoryType);
  if (!config) return {};

  return {
    title: `${config.displayName} - Pooja Booking Services`,
    description: config.description,
    keywords: [
      config.displayName.toLowerCase(),
      config.name,
      'pooja',
      'booking',
      'services',
      'ritual',
      'ceremony'
    ].join(', '),
    image: config.image,
    url: getCategoryRoutePath(categoryType)
  };
};

/**
 * Check if category supports a specific feature
 */
export const categorySupportsFeature = (categoryType, feature) => {
  const config = getCategoryConfig(categoryType);
  return config?.hasFeature(feature) || false;
};

/**
 * Get category theme configuration
 */
export const getCategoryTheme = (categoryType) => {
  const config = getCategoryConfig(categoryType);
  if (!config) return {};

  return {
    theme: config.getCustomization('theme'),
    primaryColor: config.getCustomization('primaryColor'),
    secondaryColor: config.getCustomization('secondaryColor'),
    showAdvancedFilters: config.getCustomization('showAdvancedFilters'),
    enablePriceComparison: config.getCustomization('enablePriceComparison'),
    showReviews: config.getCustomization('showReviews')
  };
};

/**
 * Filter categories by features
 */
export const getCategoriesWithFeature = (feature) => {
  return getAllCategoryConfigs()
    .filter(config => config.hasFeature(feature))
    .map(config => ({
      type: config.type,
      name: config.name,
      displayName: config.displayName,
      config: config
    }));
};

/**
 * Get category navigation menu items
 */
export const getCategoryNavigationItems = () => {
  return getAllCategoryConfigs()
    .sort((a, b) => a.getMetadata('priority') - b.getMetadata('priority'))
    .map(config => ({
      label: config.displayName,
      path: getCategoryRoutePath(config.type),
      icon: config.icon,
      priority: config.getMetadata('priority')
    }));
};

/**
 * Validate category configuration
 */
export const validateCategoryConfig = (categoryType) => {
  const config = getCategoryConfig(categoryType);
  if (!config) {
    return { valid: false, errors: ['Category configuration not found'] };
  }

  const errors = [];
  
  if (!config.id) errors.push('Category ID is required');
  if (!config.name) errors.push('Category name is required');
  if (!config.displayName) errors.push('Category display name is required');
  if (!config.type) errors.push('Category type is required');
  if (!config.description) errors.push('Category description is required');

  return {
    valid: errors.length === 0,
    errors
  };
};

/**
 * Get popular categories sorted by popularity score
 */
export const getPopularCategories = (limit = null) => {
  let categories = getAllCategoryConfigs()
    .sort((a, b) => b.getMetadata('popularityScore') - a.getMetadata('popularityScore'))
    .map(config => ({
      type: config.type,
      name: config.name,
      displayName: config.displayName,
      icon: config.icon,
      popularityScore: config.getMetadata('popularityScore'),
      path: getCategoryRoutePath(config.type)
    }));

  return limit ? categories.slice(0, limit) : categories;
};

/**
 * Search categories by name or description
 */
export const searchCategories = (searchTerm) => {
  if (!searchTerm || searchTerm.trim() === '') {
    return getAllCategoryConfigs();
  }

  const term = searchTerm.toLowerCase();
  
  return getAllCategoryConfigs().filter(config => 
    config.name.toLowerCase().includes(term) ||
    config.displayName.toLowerCase().includes(term) ||
    config.description.toLowerCase().includes(term)
  );
};
